import { Box, Chip } from '@mui/material';
import { darken, styled } from '@mui/material/styles';

// ─── Input wrapper ────────────────────────────────────────────────────────────

export const AutocompleteWrapper = styled(Box)({
  width: '100%',

  // hide the default clear + popup-indicator buttons
  '& .MuiAutocomplete-endAdornment': {
    display: 'none',
  },

  '& .MuiInputBase-root': {
    flexWrap: 'wrap',
    padding: '8px !important',
    gap: '6px',
    alignItems: 'center',
    cursor: 'text',
  },

  '& .MuiAutocomplete-input': {
    minWidth: 60,
    height: 24,
    borderRadius: '8px',
    padding: '0 !important',
  },
});

/**
 * Pass via `slotProps.paper.sx` on the Autocomplete to style the portal-rendered
 * dropdown paper and its listbox items.
 */
export const dropdownPaperSx = {
  boxShadow: '0 1px 15.3px 0 rgba(0, 62, 71, 0.10)',
  '& .MuiAutocomplete-listbox': {
    padding: '8px',
    '& .MuiAutocomplete-option': {
      padding: '8px',
      minHeight: '40px',
    },
  },
} as const;

// ─── Styled chip (selected value & option preview) ────────────────────────────

export const StyledChip = styled(Chip, {
  shouldForwardProp: (p) => p !== 'tagColor',
})<{ tagColor: string }>(({ tagColor, theme }) => ({
  height: 24,
  maxWidth: 160,
  borderRadius: '6px',
  fontSize: '0.75rem',
  margin: '0 !important',
  fontWeight: 600,
  backgroundColor: tagColor,
  color: darken(tagColor, 0.7),
  boxSizing: 'border-box',

  '& .MuiChip-label': {
    paddingInline: 8,
    fontWeight: 700,
  },

  '& .MuiChip-deleteIcon': {
    color: theme.palette.grey[50],
    fontSize: 16,
    '&:hover': { color: theme.palette.grey[500] },
  },
}));

// ─── Option row ───────────────────────────────────────────────────────────────

export const OptionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: 6,

  // reveal the ⋯ edit button only on hover
  '& .option-action': {
    opacity: 0,
    transition: 'opacity 0.12s',
  },
  '&:hover .option-action': {
    opacity: 1,
  },
});

// Right-aligned slot holding the ⋯ edit button. Fixed size so the row width
// stays stable whether or not the button is visible.
export const OptionActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,
  width: 24,
  height: 24,
});

// ─── Edit submenu (MUI Popover paper) ─────────────────────────────────────────
// Applied via `slotProps.paper.sx` on the Popover; positioning + click-outside
// are handled by the Popover itself.

// The horizontal margin (ml/mr) is applied at the call site so the gap sits on
// whichever side the submenu opens.
export const editPaperSx = {
  borderRadius: '8px',
  padding: '12px',
  marginTop: 0,
  minWidth: 210,
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
} as const;

// ─── Color swatches ───────────────────────────────────────────────────────────

export const ColorSwatchRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  alignItems: 'center',
});

export const ColorSwatch = styled(Box, {
  shouldForwardProp: (p) => p !== 'swatchColor' && p !== 'selected',
})<{ swatchColor: string; selected?: boolean }>(({ swatchColor, selected }) => ({
  width: 18,
  height: 18,
  borderRadius: 6,
  backgroundColor: swatchColor,
  cursor: 'pointer',
  flexShrink: 0,
  border:
    swatchColor === '#ffffff'
      ? `2px solid ${selected ? '#bdbdbd' : '#e0e0e0'}`
      : `2px solid ${selected ? darken(swatchColor, 0.4) : 'transparent'}`,
  boxSizing: 'border-box',
  transition: 'transform 0.12s',
  '&:hover': { transform: 'scale(1.2)' },
}));
